import React from 'react';
import { usePortal } from '../contexts/PortalContext';
import type { ClientRoute } from '../contexts/PortalContext';
import { brand } from '../data/projects';
import { ContactPortal } from './ContactPortal';

/**
 * THE PROJECT TABS ARE GONE (26 Sep 2026).
 *
 * There were five: Home · Project · Deliverables · Files · Schedule. They
 * were Portal's production model shown to the client — and the audit's
 * central finding was that a client arrives with two questions, not five
 * sections. Everything those four screens carried is on the one page now:
 * the work with its transcript, the downloads, the dates.
 *
 * They were left standing for a few hours after Overview went live, on the
 * theory that keeping the old screens behind the new one was the safe way to
 * replace something client-facing. It was not safe, it was worse: a client
 * got the new single page AND four old screens showing the same things the
 * old way, which is more to navigate than before the redesign started. Tom
 * saw it and said so. **A half-finished replacement is not a cautious
 * replacement.** Ship the new thing or don't, but do not run both at once on
 * a screen a client is looking at.
 *
 * The routes behind them still exist in ClientApp and are simply unreachable
 * from here. That is deliberate for now — removing a route means checking
 * every `goClient` call that might still aim at it (the Transcript page's
 * back link did, and had to be redirected the same day). They go in the
 * vocabulary sweep.
 */

/**
 * A show landing page keeps its two, and only because §3.7 of the audit is
 * not built yet: a show is meant to become a project whose index rows are
 * episodes, at which point this disappears too. Home and the pooled
 * cross-episode Deliverables list are the only things that mean anything at
 * show level, and without the second one there is no way to reach it.
 */
const NAV_SHOW: {
  label: string;
  route: ClientRoute;
}[] = [{
  label: 'Home',
  route: 'show'
}, {
  label: 'Deliverables',
  route: 'show-deliverables'
}];
export function ClientShell({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    clientRoute,
    goClient,
    activeProject,
    activeShow,
    changeClientName,
    clientPreview
  } = usePortal();
  const [contactOpen, setContactOpen] = React.useState(false);

  /**
   * In a staff preview the page reads exactly as the client's does, but the
   * two controls that would write or change who is signed in do nothing.
   */
  const openContact = clientPreview ? undefined : () => setContactOpen(true);
  const onShow = clientRoute === 'show' || clientRoute === 'show-deliverables';

  /* On the show landing page there is no single "current" episode yet — the
     whole point of that page is choosing one — so the footer identifies the
     show itself (client + show name) rather than whichever episode happens
     to be sitting in `activeProject` from a previous visit. Every other
     route is scoped to one specific project, where `activeProject` is
     exactly right. */
  const footerClient = onShow && activeShow ? activeShow.client : activeProject.client;
  const footerName = onShow && activeShow ? activeShow.name : activeProject.name;
  return <div className="min-h-screen w-full bg-paper text-ink" data-id="element-323" data-mp="3syc5c85supja">
      <header className="border-b border-rule" data-id="element-324" data-mp="3t0ycw7sox51i">
        <div className="mx-auto flex max-w-portal flex-col gap-5 px-6 py-6 sm:px-10 md:flex-row md:items-center md:justify-between" data-id="element-325" data-mp="3o0kfdjoptdhq">
          <button type="button" onClick={() => goClient(onShow ? 'show' : 'home')} className="block self-start text-left transition-opacity duration-150 ease-out hover:opacity-60" data-id="element-326" data-mp="phlsb9bsvc83">
            {/* The same supplied wordmark as the studio side, in its dark form. */}
            <img src={brand.lockup} alt="Portal Productions" className="h-5 w-auto sm:h-6" data-id="element-327" data-mp="ar4wc5hxpeil" />
            <span className="label-sm mt-2.5 block text-mute" data-id="element-328" data-mp="189xwm1m0knkv">Client Portal</span>
          </button>

          <div className="flex items-center justify-between gap-8 md:justify-end" data-id="element-329" data-mp="13glgkfvehp3q">
            {onShow && <nav aria-label="Client sections" data-id="element-330" data-mp="o29ghrqqk1mc">
                <ul className="m-0 flex list-none gap-6 p-0 sm:gap-8" data-id="element-331" data-mp="s2jkwzyvqjz3">
                  {NAV_SHOW.map(item => {
                const active = clientRoute === item.route;
                return <li key={item.route} data-id="element-332" data-mp="j1lgwhofycgi">
                        <button type="button" onClick={() => goClient(item.route)} aria-current={active ? 'page' : undefined} className={['label whitespace-nowrap pb-1 transition-colors duration-150 ease-out', active ? 'border-b border-ink text-ink' : 'border-b border-transparent text-mute hover:text-ink'].join(' ')} data-id="element-333" data-mp="16qef6ksjcwea">
                          {item.label}
                        </button>
                      </li>;
              })}
                </ul>
              </nav>}
            <button type="button" onClick={openContact} aria-disabled={clientPreview || undefined} className="label whitespace-nowrap text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-334" data-mp="3a56wpdndxkl7">
              Contact Portal
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-portal px-6 pb-32 pt-8 sm:px-10 sm:pt-10" data-id="element-335" data-mp="2r9grlhk5o833">{children}</main>

      <footer className="border-t border-rule" data-id="element-336" data-mp="3599ef1ska45s">
        <div className="mx-auto flex max-w-portal flex-wrap items-center justify-between gap-4 px-6 py-8 sm:px-10" data-id="element-337" data-mp="355u2g7u6pb8l">
          <p className="label m-0 text-mute" data-id="element-338" data-mp="khpixmnmdcet">
            {footerClient} · {footerName}
          </p>
          <div className="flex flex-wrap items-center gap-8" data-id="element-339" data-mp="fj4oy4yynoyt">
            {/* Kept quietly here so somebody else on this computer can take over.
                §2.7 of the audit: this is a sign-out wearing the wrong name, and
                "Contact Portal" below repeats the header. Both are in the
                vocabulary sweep — left alone here so the tab removal is one
                change and not three. */}
            <button type="button" onClick={clientPreview ? undefined : changeClientName} aria-disabled={clientPreview || undefined} className="label m-0 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-340" data-mp="3n9klmfanlxz2">
              Change Name
            </button>
            <button type="button" onClick={openContact} aria-disabled={clientPreview || undefined} className="label m-0 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-341" data-mp="2zjq77taos1cp">
              Portal Productions · Dubai
            </button>
          </div>
        </div>
      </footer>

      <ContactPortal open={contactOpen} onClose={() => setContactOpen(false)} data-id="element-342" data-mp="24awgflloddgh" />
    </div>;
}